import { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  RS_COUNTRY_CODES,
  RS_FREQUENT_COUNTRY_CODES,
  type RsCountryCode,
} from '../../constants/rsCountryCodes';
import styles from './ReservationBoardCalendar.module.css';

type RsCountrySelectProps = {
  value: RsCountryCode;
  onChange: (code: RsCountryCode) => void;
  disabled?: boolean;
  id?: string;
  t: TFunction;
  hintKey?: string;
  hintDefault?: string;
  /** sticky ツールバー向け: ラベル行を出さず aria / title だけにする */
  compact?: boolean;
};

function createRegionDisplayNames(locale: string): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    return null;
  }
}

/** 閉じたセレクト／一覧用。「JP 日本」形式 */
function formatRsCountryOptionLabel(code: string, displayNames: Intl.DisplayNames | null): string {
  const name = displayNames?.of(code);
  return name && name !== code ? `${code} ${name}` : code;
}

export default function RsCountrySelect({
  value,
  onChange,
  disabled = false,
  id = 'rs-country-code',
  t,
  hintKey = 'reservation.manager.countryCodeHint',
  hintDefault = '選択した国の祝日がカレンダーに表示されます。',
  compact = false,
}: RsCountrySelectProps) {
  const { i18n } = useTranslation();
  const hint = t(hintKey, { defaultValue: hintDefault });
  const frequentSet = useMemo(() => new Set<string>(RS_FREQUENT_COUNTRY_CODES), []);
  const displayNames = useMemo(
    () => createRegionDisplayNames(i18n.language || 'ja'),
    [i18n.language]
  );
  const otherCodes = useMemo(() => {
    const collator = new Intl.Collator(i18n.language || 'ja');
    return RS_COUNTRY_CODES.filter((code) => !frequentSet.has(code)).sort((a, b) =>
      collator.compare(displayNames?.of(a) ?? a, displayNames?.of(b) ?? b)
    );
  }, [frequentSet, displayNames, i18n.language]);
  const selectedLabel = formatRsCountryOptionLabel(value, displayNames);
  const countryLabel = t('reservation.manager.countryCode', { defaultValue: '国（祝日の反映）' });

  const select = (
    <Form.Select
      id={id}
      size="sm"
      value={value}
      disabled={disabled}
      title={`${selectedLabel} — ${hint}`}
      aria-label={`${countryLabel}: ${selectedLabel}`}
      data-rs-country-compact={compact ? 'true' : undefined}
      className={compact ? styles.countryCompactNative : undefined}
      onChange={(e) => onChange(e.target.value as RsCountryCode)}
    >
      <optgroup label={t('reservation.board.countryGroupFrequent', { defaultValue: 'よく使う' })}>
        {RS_FREQUENT_COUNTRY_CODES.map((code) => (
          <option key={code} value={code}>
            {formatRsCountryOptionLabel(code, displayNames)}
          </option>
        ))}
      </optgroup>
      <optgroup label={t('reservation.board.countryGroupAll', { defaultValue: 'その他' })}>
        {otherCodes.map((code) => (
          <option key={code} value={code}>
            {formatRsCountryOptionLabel(code, displayNames)}
          </option>
        ))}
      </optgroup>
    </Form.Select>
  );

  if (compact) {
    // 狭い sticky 帯では「JP 日本」が JP… に潰れないよう、閉じた顔は ISO コードのみ。
    // 開いた一覧・title / aria はフルラベルのまま。
    return (
      <div className={styles.countryCompactSelect}>
        <span className={styles.countryCompactFace} aria-hidden>
          {value}
        </span>
        {select}
      </div>
    );
  }

  return (
    <Form.Group className="mb-2">
      <Form.Label htmlFor={id} className="mb-1 small">
        {countryLabel}
      </Form.Label>
      {select}
    </Form.Group>
  );
}
